import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { projects } from '../data/projects';

const getLocalized = (project: any, field: string, lang: string): string => {
  if (lang === 'zh' && project[`${field}_zh`]) return project[`${field}_zh`];
  if (lang === 'en' && project[`${field}_en`]) return project[`${field}_en`];
  return project[field] || '';
};

const ProjectGrid: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [selectedProject, setSelectedProject] = useState<typeof projects[0] | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [galleryPlaying, setGalleryPlaying] = useState(false);
  const [galleryReady, setGalleryReady] = useState(false);
  const frameDurations = selectedProject && 'galleryFrameDurations' in selectedProject
    ? selectedProject.galleryFrameDurations : undefined;

  // Load the entire sequence before starting the short animation frames.
  useEffect(() => {
    let cancelled = false;
    setGalleryReady(false);
    if (!selectedProject || !('galleryFrameDurations' in selectedProject)) return;
    Promise.all(selectedProject.images.map(src => new Promise<void>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`Unable to load ${src}`));
      image.src = src;
    }))).then(() => {
      if (!cancelled) setGalleryReady(true);
    }).catch(() => {
      if (!cancelled) setGalleryPlaying(false);
    });
    return () => { cancelled = true; };
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject || !frameDurations || !galleryPlaying || !galleryReady) return;
    const timer = window.setTimeout(() => {
      setCurrentImageIndex(index => (index + 1) % selectedProject.images.length);
    }, frameDurations[currentImageIndex] ?? 3000);
    return () => window.clearTimeout(timer);
  }, [selectedProject, frameDurations, currentImageIndex, galleryPlaying, galleryReady]);

  const openProject = (project: typeof projects[0]) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    setGalleryPlaying('galleryFrameDurations' in project);
    document.body.style.overflow = 'hidden';
  };

  const closeProject = () => {
    setSelectedProject(null);
    document.body.style.overflow = '';
  };

  // Ensure overflow is restored on unmount or route change
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedProject) {
      setGalleryPlaying(false);
      setCurrentImageIndex((prev) => (prev + 1) % selectedProject.images.length);
    }
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedProject) {
      setGalleryPlaying(false);
      setCurrentImageIndex((prev) => (prev - 1 + selectedProject.images.length) % selectedProject.images.length);
    }
  };

  useEffect(() => {
    const handleOpenProject = (e: CustomEvent) => {
      const project = projects.find(p => p.id === e.detail.id);
      if (project) {
        openProject(project);
        // Scroll to projects section
        const element = document.getElementById('projects');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    window.addEventListener('open-project', handleOpenProject as EventListener);
    return () => window.removeEventListener('open-project', handleOpenProject as EventListener);
  }, []);

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex justify-between items-end mb-12">
          <h3 className="text-xl font-medium">{t('projects.title')}</h3>
          <a href="#" className="text-sm text-gray-500 hover:text-black transition-colors">
            {t('projects.all')} &rarr;
          </a>
        </div>

        {/* Grid Layout - Consistent Size (Cover) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {projects.map((project) => (
            <div 
              key={project.id} 
              className="group cursor-pointer flex flex-col"
              onClick={() => openProject(project)}
            >
              {/* Image Container: Fixed Aspect Ratio with Cover */}
              <div className="relative w-full aspect-[4/3] bg-gray-100 mb-4 overflow-hidden">
                <img
                  src={project.image}
                  alt={getLocalized(project, 'title', lang)}
                  className={`w-full h-full transition-transform duration-700 ease-out group-hover:scale-105 ${
                    (project as any).coverFit === 'contain' ? 'object-contain bg-gray-50' : 'object-cover'
                  }`}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
              </div>

              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-lg font-medium group-hover:underline decoration-1 underline-offset-4">
                    {getLocalized(project, 'title', lang)}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {project.location} — {project.year}
                  </p>
                </div>
                <span className="text-[10px] uppercase tracking-wider border border-gray-200 px-2 py-0.5 rounded text-gray-400">
                  {project.type}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-md overflow-y-auto"
          >
            <div className="min-h-screen flex flex-col">
              {/* Modal Header */}
              <div className="sticky top-0 z-10 flex justify-between items-center p-6 md:p-8 bg-white/80 backdrop-blur-sm">
                <h2 className="text-2xl font-medium">{getLocalized(selectedProject, 'title', lang)}</h2>
                <button 
                  onClick={closeProject}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 container mx-auto px-4 md:px-8 pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  
                  {/* Left Column: Images */}
                  <div className="lg:col-span-8 space-y-4">
                    {frameDurations && (
                      <button
                        type="button"
                        onClick={() => setGalleryPlaying(playing => !playing)}
                        aria-label={galleryPlaying ? (lang === 'zh' ? '暂停播放' : lang === 'en' ? 'Pause slideshow' : 'Mettre en pause') : (lang === 'zh' ? '播放图集' : lang === 'en' ? 'Play slideshow' : 'Lire le diaporama')}
                        className="flex items-center gap-2 text-sm border border-gray-200 px-3 py-2 hover:bg-gray-100"
                      >
                        {galleryPlaying ? <Pause size={16} /> : <Play size={16} />}
                        {galleryPlaying ? (lang === 'zh' ? '暂停播放' : lang === 'en' ? 'Pause slideshow' : 'Mettre en pause') : (lang === 'zh' ? '播放图集' : lang === 'en' ? 'Play slideshow' : 'Lire le diaporama')}
                      </button>
                    )}
                    <div className="relative bg-gray-100 overflow-hidden group flex items-center justify-center" style={{ minHeight: '50vh', maxHeight: '75vh' }}>
                      <img
                        src={selectedProject.images[currentImageIndex]}
                        alt={getLocalized(selectedProject, 'title', lang)}
                        className="max-w-full max-h-[75vh] object-contain"
                      />
                      
                      {selectedProject.images.length > 1 && (
                        <>
                          <button 
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <ChevronLeft size={20} />
                          </button>
                          <button 
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <ChevronRight size={20} />
                          </button>
                        </>
                      )}
                    </div>
                    
                    {/* Thumbnails */}
                    {selectedProject.images.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {selectedProject.images.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={() => { setGalleryPlaying(false); setCurrentImageIndex(idx); }}
                            className={`relative flex-shrink-0 w-24 h-16 overflow-hidden ${
                              currentImageIndex === idx ? 'ring-2 ring-black' : 'opacity-60 hover:opacity-100'
                            }`}
                          >
                            <img src={img} alt="" className="w-full h-full object-contain bg-gray-100" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right Column: Details */}
                  <div className="lg:col-span-4 space-y-8">
                    <div>
                      <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 mb-4">
                        {t('project.details')}
                      </h3>
                      <div className="space-y-4 text-sm">
                        <div className="grid grid-cols-3 border-b border-gray-100 pb-2">
                          <span className="text-gray-500">{t('project.location')}</span>
                          <span className="col-span-2">{selectedProject.location}</span>
                        </div>
                        <div className="grid grid-cols-3 border-b border-gray-100 pb-2">
                          <span className="text-gray-500">{t('project.year')}</span>
                          <span className="col-span-2">{selectedProject.year}</span>
                        </div>
                        <div className="grid grid-cols-3 border-b border-gray-100 pb-2">
                          <span className="text-gray-500">{t('project.type')}</span>
                          <span className="col-span-2">{selectedProject.type}</span>
                        </div>
                        {selectedProject.client && (
                          <div className="grid grid-cols-3 border-b border-gray-100 pb-2">
                            <span className="text-gray-500">Client</span>
                            <span className="col-span-2">{selectedProject.client}</span>
                          </div>
                        )}
                        {selectedProject.surface && (
                          <div className="grid grid-cols-3 border-b border-gray-100 pb-2">
                            <span className="text-gray-500">Surface</span>
                            <span className="col-span-2">{selectedProject.surface}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 mb-4">
                        {t('project.description')}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {getLocalized(selectedProject, 'description', lang)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProjectGrid;
